import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FileText, DollarSign, Key, Calculator, Briefcase, Home } from "lucide-react";

const iconOptions = [
  { value: "FileText", label: "Document", icon: FileText },
  { value: "DollarSign", label: "Finance", icon: DollarSign },
  { value: "Key", label: "Key", icon: Key },
  { value: "Calculator", label: "Calculator", icon: Calculator },
  { value: "Briefcase", label: "Briefcase", icon: Briefcase },
  { value: "Home", label: "Home", icon: Home },
];

interface AddCategoryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit?: (data: { title: string; icon: string }) => void;
}

export function AddCategoryDialog({ open, onOpenChange, onSubmit }: AddCategoryDialogProps) {
  const [title, setTitle] = useState("");
  const [icon, setIcon] = useState("FileText");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit?.({ title, icon });
    setTitle("");
    setIcon("FileText");
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="dialog-add-category">
        <DialogHeader>
          <DialogTitle>Add New Category</DialogTitle>
          <DialogDescription>
            Create a new checklist category for estate planning.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="category-title">Category Name *</Label>
              <Input
                id="category-title"
                placeholder="e.g., Healthcare Documents"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
                data-testid="input-category-title"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="category-icon">Icon</Label>
              <Select value={icon} onValueChange={setIcon}>
                <SelectTrigger id="category-icon" data-testid="select-category-icon">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {iconOptions.map((option) => {
                    const Icon = option.icon;
                    return (
                      <SelectItem key={option.value} value={option.value}>
                        <div className="flex items-center gap-2">
                          <Icon className="h-4 w-4" />
                          <span>{option.label}</span>
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="button-cancel">
              Cancel
            </Button>
            <Button type="submit" data-testid="button-submit">Add Category</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
